export const DIFFICULTIES = ['easy', 'medium', 'hard'] as const;
export type Difficulty = (typeof DIFFICULTIES)[number];

export const CATEGORIES = [
  'animal',
  'food',
  'object',
  'place',
  'action',
  'nature',
  'vehicle',
  'sport',
] as const;
export type Category = (typeof CATEGORIES)[number];

export const CATEGORY_LABEL: Record<Category, string> = {
  animal: 'Animal',
  food: 'Food',
  object: 'Object',
  place: 'Place',
  action: 'Action',
  nature: 'Nature',
  vehicle: 'Vehicle',
  sport: 'Sport',
};

export interface DrawGuessSettings {
  enabled: boolean;
  roundDurationSeconds: number;
  maxWinnersPerRound: number;
  gemRewardFirst: number;
  gemRewardSecond: number;
  gemRewardThird: number;
  showLetterCount: boolean;
  showCategory: boolean;
  chatCommand: string;
  cooldownSeconds: number;
}

export const DEFAULT_SETTINGS: DrawGuessSettings = {
  enabled: true,
  roundDurationSeconds: 90,
  maxWinnersPerRound: 3,
  gemRewardFirst: 500,
  gemRewardSecond: 250,
  gemRewardThird: 100,
  showLetterCount: true,
  showCategory: true,
  chatCommand: '!draw',
  cooldownSeconds: 60,
};

export interface DrawGuessWord {
  id: string;
  word: string;
  category: Category;
  difficulty: Difficulty;
}

export type Phase = 'idle' | 'drawing' | 'reveal';

export interface Guess {
  username: string;
  userId: string | null;
  message: string;
  isCorrect: boolean;
  timestamp: number;
}

export interface Winner {
  username: string;
  userId: string | null;
  placement: 1 | 2 | 3;
  gemsAwarded: number;
  timeToGuessMs: number;
}

export interface RoundSnapshot {
  streamerId: string;
  roundId: string;
  phase: Phase;
  /** Word is null until reveal phase — overlay must hide it pre-reveal. */
  word: string | null;
  category: Category | null;
  /** Underscored hint `_ _ _ _ _` when `showLetterCount` is on, else null. */
  maskedWord: string | null;
  /** CDN URL for the cached/generated drawing. Null only briefly during boot. */
  imageUrl: string | null;
  /** 0..totalTiles. Increments on a timer; equals totalTiles on reveal. */
  revealedTiles: number;
  totalTiles: number;
  /** Stable 0..N-1 permutation seeded by roundId — client + server agree. */
  tileOrder: number[];
  recentGuesses: Guess[];
  winners: Winner[];
  startedAt: number;
  /** ms remaining; recomputed on each publish so overlay clock stays in sync. */
  timeRemainingMs: number;
  durationMs: number;
}

/** Normalize a guess or a word for equality comparison. */
export function normalizeGuess(s: string): string {
  return s
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9 ]/g, '')
    .replace(/\s+/g, ' ');
}

/**
 * Deterministic Fisher–Yates shuffle. Used so server + overlay derive the
 * same tile order from the same roundId. Tiny mulberry32 PRNG seeded by a
 * 32-bit hash of the roundId.
 */
export function deterministicTileOrder(roundId: string, total: number): number[] {
  let h = 2166136261;
  for (let i = 0; i < roundId.length; i++) {
    h ^= roundId.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  let state = h >>> 0 || 1;
  const rand = () => {
    state |= 0;
    state = (state + 0x6d2b79f5) | 0;
    let t = Math.imul(state ^ (state >>> 15), 1 | state);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
  const arr = Array.from({ length: total }, (_, i) => i);
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [arr[i], arr[j]] = [arr[j]!, arr[i]!];
  }
  return arr;
}

export function maskWord(word: string): string {
  return word
    .split('')
    .map((c) => (/[a-zA-Z]/.test(c) ? '_' : c))
    .join(' ');
}
